import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGS } from "@/i18n/content";
import { splitLang, isLocalizable, withLang } from "@/lib/i18nPath";

const SUPPORTED = LANGS.map((l) => l.code);
const STORAGE_KEY = "vornx-lang";
export const CHOICE_KEY = "vornx-lang-choice";
const DEFAULT_LANG = "it"; // Italian is the primary language of VORNX (root URLs)

const LanguageContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: () => "", langs: LANGS });

// Public pages: language comes from the URL (/en/..., root = it). Private pages keep the last language used.
export const LanguageProvider = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { lang: urlLang, base } = splitLang(pathname);
  const localizable = isLocalizable(base);
  const [stored, setStored] = useState(() => {
    const s = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    return SUPPORTED.includes(s) ? s : DEFAULT_LANG;
  });
  const lang = localizable ? urlLang || DEFAULT_LANG : stored;

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
    setStored(lang);
  }, [lang]);

  const setLang = (code) => {
    if (!SUPPORTED.includes(code)) return;
    window.localStorage.setItem(CHOICE_KEY, code);
    if (localizable) navigate(withLang(base, code) + search + hash);
    else setStored(code);
  };

  // Resolves a localized node ({it,en,de,es,fr}) with graceful fallback to Italian, then English.
  const t = (node) => {
    if (node && typeof node === "object" && !Array.isArray(node)) {
      const isLangNode = SUPPORTED.some((c) => node[c] !== undefined);
      if (isLangNode) return node[lang] ?? node.it ?? node.en ?? "";
    }
    return node;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
